import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MaterialReactTable,
  createRow,
  useMaterialReactTable,
} from "material-react-table";
import React, { useMemo, useState } from "react";
import {
  RESOURCE_UNIT_OPTIONS,
  createResource,
  deleteResource,
  normalizeResourceUnit,
  updateResource,
} from "./resourceApi";
import { useResources } from "./useResources";

const INITIAL_ROW = { name: "", rate: 0, unit: "hr" };

const validateResource = (values) => {
  const errors = {};
  if (!String(values.name ?? "").trim()) {
    errors.name = "Name is required";
  }
  const rate = Number(values.rate);
  if (!Number.isFinite(rate)) {
    errors.rate = "Rate must be a number";
  }
  const unit = normalizeResourceUnit(values.unit);
  if (!RESOURCE_UNIT_OPTIONS.includes(unit)) {
    errors.unit = "Unit must be hr or $";
  }
  return errors;
};

export default function ResourcesModal({
  open,
  onClose,
  task = null,
  onResourcesChange,
}) {
  const { resources, isLoading, invalidate } = useResources({ enabled: open });
  const [validationErrors, setValidationErrors] = useState({});
  const [deletingId, setDeletingId] = useState(null);

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 200,
        muiEditTextFieldProps: {
          error: !!validationErrors.name,
          helperText: validationErrors.name,
        },
      },
      {
        accessorKey: "rate",
        header: "Rate",
        size: 100,
        muiEditTextFieldProps: {
          type: "number",
          inputProps: { min: 0, step: 0.01 },
          error: !!validationErrors.rate,
          helperText: validationErrors.rate,
        },
        Cell: ({ cell }) => {
          const n = Number(cell.getValue());
          return Number.isFinite(n) ? n.toFixed(2) : "0.00";
        },
      },
      {
        accessorKey: "unit",
        header: "Unit",
        size: 80,
        muiEditTextFieldProps: {
          select: true,
          error: !!validationErrors.unit,
          helperText: validationErrors.unit,
          children: RESOURCE_UNIT_OPTIONS.map((unit) => (
            <MenuItem key={unit} value={unit}>
              {unit}
            </MenuItem>
          )),
        },
        Cell: ({ cell }) => {
          const unit = cell.getValue();
          return unit === "$" ? "$" : "hr";
        },
      },
    ],
    [validationErrors],
  );

  const refresh = async () => {
    await invalidate();
    onResourcesChange?.();
  };

  const table = useMaterialReactTable({
    columns,
    data: resources,
    getRowId: (row) => String(row.id),
    enableEditing: true,
    editDisplayMode: "row",
    createDisplayMode: "row",
    enableCreatingRow: true,
    positionCreatingRow: "bottom",
    enableColumnActions: false,
    enableSorting: true,
    enableRowActions: true,
    positionActionsColumn: "first",
    state: { isLoading },
    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: "flex", gap: 0.5 }}>
        <Tooltip title="Edit">
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            color="error"
            disabled={deletingId === row.original.id}
            onClick={async () => {
              const id = row.original.id;
              if (
                !window.confirm(
                  `Delete resource "${row.original.name}"? Linked tasks will be unassigned.`,
                )
              ) {
                return;
              }
              setDeletingId(id);
              try {
                await deleteResource(id);
                await refresh();
              } finally {
                setDeletingId(null);
              }
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    onCreatingRowSave: async ({ values, table }) => {
      const errors = validateResource(values);
      if (Object.keys(errors).length) {
        setValidationErrors(errors);
        return;
      }
      setValidationErrors({});
      await createResource({
        name: values.name,
        rate: values.rate,
        unit: values.unit,
      });
      await refresh();
      table.setCreatingRow(null);
    },
    onEditingRowSave: async ({ values, table, row }) => {
      const errors = validateResource({ ...row.original, ...values });
      if (Object.keys(errors).length) {
        setValidationErrors(errors);
        return;
      }
      setValidationErrors({});
      await updateResource(row.original.id, {
        name: values.name,
        rate: values.rate,
        unit: values.unit,
      });
      await refresh();
      table.setEditingRow(null);
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Button
        variant="contained"
        size="small"
        startIcon={<AddIcon />}
        onClick={() =>
          table.setCreatingRow(createRow(table, { ...INITIAL_ROW }))
        }
      >
        Add resource
      </Button>
    ),
    muiEditTextFieldProps: {
      variant: "standard",
      size: "small",
      fullWidth: true,
    },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        PCS Resources
        {task?.task ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Task: {task.task}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Manage billable resources (people, roles, or cost units). Assign them
          to tasks using the Resource column in the task table.
        </Typography>
        <MaterialReactTable table={table} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
